import {
  TemplatePreset,
  TemplatePresetMetadata,
  PassTemplateInput,
} from '../../store/types/template';
import loyaltyTemplateData from './loyalty.json';
import stampCardTemplateData from './stampCard.json';
import eventTicketTemplateData from './eventTicket.json';
import transitTemplateData from './transit.json';
import membershipTemplateData from './membership.json';
import insuranceTemplateData from './insurance.json';
import insuranceMeridianBlueTemplateData from './insuranceMeridianBlue.json';
import couponCoffeeTemplateData from './couponCoffee.json';
import stampCardCoffeeTemplateData from './stampCardCoffee.json';
import membershipAlumniTemplateData from './membershipAlumni.json';
import accessStaffTemplateData from './accessStaff.json';
import accessStaffGenericTemplateData from './accessStaffGeneric.json';
import designConferenceTemplateData from './designConference.json';
import pilatesStudioTemplateData from './pilatesStudio.json';
import paperSaintsTemplateData from './paperSaints.json';
import giftCardFloristTemplateData from './giftCardFlorist.json';
import hotelPrivilegesTemplateData from './hotelPrivileges.json';
import shoppingCentreTemplateData from './shoppingCentre.json';
import gymMembershipTemplateData from './gymMembership.json';
import seasonTicketTemplateData from './seasonTicket.json';

const TEMPLATE_DATA_BY_ID: Record<string, PassTemplateInput> = {
  'loyalty-supermarket': loyaltyTemplateData as unknown as PassTemplateInput,
  'stamp-card-pizza': stampCardTemplateData as unknown as PassTemplateInput,
  'event-ticket-stadium':
    eventTicketTemplateData as unknown as PassTemplateInput,
  'transit-airline': transitTemplateData as unknown as PassTemplateInput,
  'membership-club': membershipTemplateData as unknown as PassTemplateInput,
  'insurance-motor-keel': insuranceTemplateData as unknown as PassTemplateInput,
  'insurance-policy-meridian-blue':
    insuranceMeridianBlueTemplateData as unknown as PassTemplateInput,
  'coupon-coffee-common-grounds':
    couponCoffeeTemplateData as unknown as PassTemplateInput,
  'stamp-card-coffee-common-grounds':
    stampCardCoffeeTemplateData as unknown as PassTemplateInput,
  'membership-alumni-westbridge':
    membershipAlumniTemplateData as unknown as PassTemplateInput,
  'access-staff-harbour-co':
    accessStaffTemplateData as unknown as PassTemplateInput,
  'access-staff-harbour-co-generic':
    accessStaffGenericTemplateData as unknown as PassTemplateInput,
  'event-ticket-design-conference':
    designConferenceTemplateData as unknown as PassTemplateInput,
  'membership-pilates-zen':
    pilatesStudioTemplateData as unknown as PassTemplateInput,
  'event-ticket-concert-paper-saints':
    paperSaintsTemplateData as unknown as PassTemplateInput,
  'coupon-gift-card-maison-blume':
    giftCardFloristTemplateData as unknown as PassTemplateInput,
  'hotel-privileges-auric':
    hotelPrivilegesTemplateData as unknown as PassTemplateInput,
  'loyalty-shopping-centre-linden':
    shoppingCentreTemplateData as unknown as PassTemplateInput,
  'membership-gym-lift-house':
    gymMembershipTemplateData as unknown as PassTemplateInput,
  'event-ticket-season-eastfield':
    seasonTicketTemplateData as unknown as PassTemplateInput,
};

const PRESET_METADATA: TemplatePresetMetadata[] = [
  {
    id: 'loyalty-shopping-centre-linden',
    name: 'Shopping centre rewards',
    categories: ['Retail'],
    useCases: ['loyalty_cards'],
    description:
      'A rewards card for shopping centres and outlet villages with points, free parking for members and offers redeemed by QR in any store.',
    bestFor: [
      'Shopping centres, malls and outlet villages',
      'Rewarding visits and purchases across many stores with one card',
      'Member perks like free parking and centre-wide offers',
    ],
    features: [
      'Points balance tracking',
      'QR code redemption in store',
      'Real-time offers',
    ],
  },
  {
    id: 'insurance-policy-meridian-blue',
    name: 'Health insurance card',
    categories: ['Insurance'],
    useCases: ['membership_passes'],
    description:
      'A health plan card with the member and plan details up front, claims and nurse line links on the back, and a visible renewal date.',
    bestFor: [
      'Health insurers making claims and verification effortless',
      'Members who need their plan details at the clinic reception',
      'Driving renewals with a visible date and pass notifications',
    ],
    features: ['Claims and nurse line links', 'Renewal reminders', 'QR code'],
  },
  {
    id: 'stamp-card-coffee-common-grounds',
    name: 'Coffee stamp card',
    categories: ['Hospitality'],
    useCases: ['loyalty_cards', 'coupons'],
    description:
      'A six-stamp coffee card where the banner fills up with every visit until the reward is earned.',
    bestFor: [
      'Cafés and coffee shops replacing paper stamp cards',
      'Venues where customers visit regularly and expect a reward',
      'Showing visual progress towards a free item',
    ],
    features: [
      'Visual progress (banner changes)',
      'Milestone notifications',
      'QR code',
    ],
  },
  {
    id: 'membership-gym-lift-house',
    name: 'Gym membership',
    categories: ['Sports & fitness'],
    useCases: ['membership_passes', 'access_passes'],
    description:
      'A gym membership scanned at the door, with the member and plan up front and the renewal date always visible.',
    bestFor: [
      'Any gym, fitness club or studio checking members in at the door',
      'Replacing plastic membership cards and fobs',
      'Driving renewals with a visible date and pass notifications',
    ],
    features: [
      'QR code door access',
      'Real-time plan updates',
      'Renewal reminders',
    ],
  },
  {
    id: 'event-ticket-concert-paper-saints',
    name: 'Concert ticket',
    categories: ['Events'],
    useCases: ['event_tickets'],
    description:
      'A concert ticket with the ticket type up front and the city, date and doors at a glance.',
    bestFor: [
      'Music venues, promoters and touring artists',
      'Any concert or live show, from club nights to arenas',
      'General admission shows scanning tickets at the door',
    ],
    features: ['QR code entry', 'Real-time event updates', 'Geofencing'],
  },
  {
    id: 'hotel-privileges-auric',
    name: 'Hotel guest perks',
    categories: ['Hospitality', 'Travel & Transit'],
    useCases: ['access_passes'],
    description:
      'A guest privileges pass covering dining, spa, pool and bar entitlements, scanned at each venue during the stay.',
    bestFor: [
      'Hotels and resorts bundling stay perks into one pass',
      'Replacing paper vouchers for dining credits and facility access',
      'Verifying guest entitlements with a scan at each outlet',
    ],
    features: ['QR code', 'Real-time perk updates', 'Multi-language support'],
  },
  {
    id: 'loyalty-supermarket',
    name: 'Supermarket loyalty card',
    categories: ['Retail'],
    useCases: ['loyalty_cards'],
    description:
      'A points-based loyalty pass for grocery stores and retail chains. Customers earn points with every purchase and see their balance update in real time.',
    bestFor: [
      'Grocery stores and supermarkets with frequent repeat customers',
      'Retail chains offering member discounts and exclusive offers',
      'Stores with in-app or in-store point redemption programs',
    ],
    features: ['Points balance tracking', 'Real-time offers', 'Barcode'],
  },
  {
    id: 'event-ticket-stadium',
    name: 'Sports match ticket',
    categories: ['Sports & fitness', 'Events'],
    useCases: ['event_tickets'],
    description:
      'An event ticket with seating details, venue information, and real-time updates for game day.',
    bestFor: [
      'Sports venues, arenas and stadiums',
      'Events where attendees need precise seating and entry information',
      'Venues wanting to send real-time updates (gate changes, delays, post-game info)',
    ],
    features: ['Geofencing', 'Real-time event updates', 'Venue app link'],
  },
  {
    id: 'membership-pilates-zen',
    name: 'Fitness class pass',
    categories: ['Sports & fitness'],
    useCases: ['membership_passes'],
    description:
      'A class-pack membership pass that tracks remaining classes up front, with the plan and expiry at a glance and a booking link on the back.',
    bestFor: [
      'Pilates, yoga and fitness studios selling class packs',
      'Studios reminding members to book before their pack expires',
      'Replacing paper punch cards with a live class balance',
    ],
    features: [
      'Class balance tracking',
      'Booking link on the back',
      'Expiry reminders',
    ],
  },
  {
    id: 'event-ticket-design-conference',
    name: 'Conference badge',
    categories: ['Events'],
    useCases: ['event_tickets'],
    description:
      'A conference attendee pass with the ticket type up front and the attendee, dates and venue at a glance.',
    bestFor: [
      'Conferences, expos and multi-day industry events',
      'Events checking attendees in by ticket type or access level',
      'Organisers sending schedule or venue updates before the day',
    ],
    features: ['QR code check-in', 'Real-time schedule updates', 'Geofencing'],
  },
  {
    id: 'coupon-gift-card-maison-blume',
    name: 'Florist gift card',
    categories: ['Retail'],
    useCases: ['coupons'],
    description:
      'A gift card with the remaining balance front and centre, a card number for online redemption, and the terms on the back.',
    bestFor: [
      'Florists, boutiques and independent retailers selling gift cards',
      'Replacing plastic gift cards with a digital balance customers always have',
      'Driving gift recipients into new first-time purchases',
    ],
    features: [
      'Balance tracking with updates',
      'Online redemption link',
      'QR code',
    ],
  },
  {
    id: 'access-staff-harbour-co',
    name: 'Staff pass',
    categories: ['Workplace & education'],
    useCases: ['access_passes'],
    description:
      'A staff pass with the employee number and role up front, and department, HR and emergency contacts on the back.',
    bestFor: [
      'Businesses issuing staff passes for entry and verification',
      'Multi-site teams where role and location matter at the door',
      'Keeping HR and emergency contacts in the pass',
    ],
    features: ['QR code', 'Clickable contact links', 'Real-time updates'],
  },
  {
    id: 'stamp-card-pizza',
    name: 'Pizza stamp card',
    categories: ['Hospitality', 'Retail'],
    useCases: ['loyalty_cards'],
    description:
      'A stamp card that rewards customers for repeat visits with a free item after 8 purchases.',
    bestFor: [
      'Any business replacing physical stamp cards with digital passes',
      'Hospitality, beauty, services, retail, classes and experiences',
      'Venues where customers visit regularly and you want to reward loyalty',
    ],
    features: [
      'Visual progress (banner changes)',
      'Milestone notifications',
      'QR code',
    ],
  },
  {
    id: 'membership-club',
    name: 'Members club card',
    categories: ['Hospitality'],
    useCases: ['membership_passes', 'access_passes'],
    description:
      'A membership pass that verifies member status, access level, and tenure with a secure QR code.',
    bestFor: [
      'Clubs, lounges, gyms and exclusive membership organisations',
      'Businesses offering tiered membership benefits (bronze, silver, gold)',
      'Venues requiring secure access verification at entry',
    ],
    features: [
      'QR code for access control',
      'Real-time tier updates',
      'Member portal link',
    ],
  },
  {
    id: 'event-ticket-season-eastfield',
    name: 'Season ticket',
    categories: ['Sports & fitness'],
    useCases: ['event_tickets'],
    description:
      'A season ticket with the holder, seat and gate up front, scanned at the turnstile every matchday.',
    bestFor: [
      'Football, rugby and any club selling season seats',
      'Replacing plastic season cards at the turnstile',
      'Reaching fans with fixtures and offers all season',
    ],
    features: [
      'QR code turnstile entry',
      'Real-time matchday updates',
      'Seat resale link',
    ],
  },
  {
    id: 'coupon-coffee-common-grounds',
    name: 'Coffee shop coupon',
    categories: ['Hospitality'],
    useCases: ['coupons'],
    description:
      'A single-offer coupon with the deal front and centre, a redemption code and expiry, and the terms on the back.',
    bestFor: [
      'Cafés, restaurants and bars running limited-time offers',
      'Seasonal promotions with a clear expiry date',
      'Driving repeat visits with redeemable in-store deals',
    ],
    features: [
      'QR code redemption',
      'Real-time offer updates',
      'Expiry reminders',
    ],
  },
  {
    id: 'access-staff-harbour-co-generic',
    name: 'Employee pass',
    categories: ['Workplace & education'],
    useCases: ['access_passes'],
    description:
      'A staff pass with the employee number and role up front, and department, HR and emergency contacts on the back.',
    bestFor: [
      'Businesses issuing staff passes for entry and verification',
      'Multi-site teams where role and location matter at the door',
      'Keeping HR and emergency contacts in the pass',
    ],
    features: ['QR code', 'Clickable contact links', 'Real-time updates'],
  },
  {
    id: 'membership-alumni-westbridge',
    name: 'Alumni card',
    categories: ['Workplace & education'],
    useCases: ['membership_passes'],
    description:
      'An alumni membership pass with the graduate number up front and benefits, website and alumni office contacts on the back.',
    bestFor: [
      'Universities and schools issuing alumni or member passes',
      'Organisations offering member benefits behind a verified pass',
      'Keeping a lifelong touchpoint in the graduate’s wallet',
    ],
    features: ['QR code', 'Benefit links on the back', 'Real-time updates'],
  },
  {
    id: 'transit-airline',
    name: 'Airline boarding pass',
    categories: ['Travel & Transit'],
    useCases: ['boarding_passes'],
    description:
      'A boarding pass with flight details, seat assignment, and real-time status updates for travelers.',
    bestFor: [
      'Airlines, rail operators and transportation providers',
      'Any travel experience requiring passenger verification and seat assignment',
      'International travel (supports multiple languages for global audiences)',
    ],
    features: ['Real-time status updates', 'Multi-language support', 'Barcode'],
  },
  {
    id: 'insurance-motor-keel',
    name: 'Motor insurance card',
    categories: ['Insurance'],
    useCases: ['membership_passes'],
    description:
      'A motor insurance policy card with the key policy details on the front and claims contacts on the back.',
    bestFor: [
      'Insurers and brokers replacing paper policy documents',
      'Policies where customers need claims contacts at the roadside',
      'Keeping policy numbers and renewal dates in the customer’s wallet',
    ],
    features: ['Claims contact links', 'Real-time policy updates', 'QR code'],
  },
];

export const TEMPLATE_PRESETS: TemplatePreset[] = PRESET_METADATA.map(
  (metadata) => ({
    metadata,
    template_data: TEMPLATE_DATA_BY_ID[metadata.id],
  })
);

export const getPresetById = (id: string): TemplatePreset | undefined =>
  TEMPLATE_PRESETS.find((preset) => preset.metadata.id === id);
